import { Headphones, ShieldCheck, Zap } from 'lucide-react';
import { useBranding } from '@/features/auth/api';

interface AuthLayoutProps {
  /** Title rendered above the form (e.g. "Welcome back", "Reset your password"). */
  title: string;
  description?: string;
  /** Footer slot under the card for links like "Back to sign in". */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared corporate split layout for unauthenticated routes (Login, Reset
 * Password, Update Password). Left panel is a branded marketing surface
 * pulled from the live `/api/obd/branding/<host>` response. Right panel
 * hosts the per-page form (`children`).
 */
export function AuthLayout({ title, description, footer, children }: AuthLayoutProps) {
  const branding = useBranding();
  const company = branding.data?.company ?? 'OBD3';

  return (
    // h-screen because body has overflow:hidden globally. The right pane
    // scrolls internally if its form is taller than the viewport.
    <div className="grid h-screen w-full lg:grid-cols-[1.05fr_1fr]">
      {/* ---- Left hero ----------------------------------------------- */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-primary text-primary-foreground lg:flex">
        {/* Background layers — subtle but deeper than a flat fill.       */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80"
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]"
        />
        <div
          aria-hidden
          className="absolute -left-32 -top-32 h-80 w-80 rounded-full bg-white/10 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-white/10 blur-3xl"
        />
        {/* Faint diagonal seam — gives the panel a "carved" edge. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent"
        />

        {/* Brand row */}
        <div className="relative z-10 flex items-center gap-2.5 p-10">
          {branding.data?.logoUrl ? (
            <img src={branding.data.logoUrl} alt={company} className="h-9 w-auto" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20 backdrop-blur">
              <div className="h-4 w-4 rounded-sm bg-primary-foreground" />
            </div>
          )}
          <span className="text-lg font-semibold tracking-tight">{company}</span>
        </div>

        {/* Hero copy + features */}
        <div className="relative z-10 space-y-10 px-10">
          <div className="space-y-4">
            {/*
             * `text-primary-foreground` is required because globals.css
             * forces every <h1> to `text-foreground` (the page-level dark
             * color). Without it the heading vanishes on the dark hero.
             */}
            <h1 className="max-w-xl text-4xl font-semibold leading-[1.1] tracking-tight text-primary-foreground">
              Enterprise voice campaigns,{' '}
              <span className="text-primary-foreground/70">delivered with precision.</span>
            </h1>
            <p className="max-w-lg text-[15px] leading-relaxed text-primary-foreground/70">
              Manage outbound voice, SMS, and IVR workflows from a single
              control plane built for operators at scale.
            </p>
          </div>

          <ul className="space-y-4">
            {FEATURES.map((feature) => (
              <li key={feature.title} className="flex items-start gap-3.5">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15 backdrop-blur">
                  <feature.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary-foreground">
                    {feature.title}
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-primary-foreground/65">
                    {feature.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer line */}
        <div className="relative z-10 flex items-center justify-between p-10 text-xs text-primary-foreground/55">
          <span>© {new Date().getFullYear()} {company}. All rights reserved.</span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.18)]" />
            All systems operational
          </span>
        </div>
      </aside>

      {/* ---- Right form panel --------------------------------------- */}
      <main className="relative flex min-h-0 flex-col items-center justify-center overflow-y-auto bg-background px-6 py-12 sm:px-12">
        {/* Mobile brand row — visible only on small screens where the
            left panel is hidden, so the user still sees the company name. */}
        <div className="absolute left-6 top-6 flex items-center gap-2 text-foreground lg:hidden">
          {branding.data?.logoUrl ? (
            <img src={branding.data.logoUrl} alt={company} className="h-7 w-auto" />
          ) : (
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <div className="h-3 w-3 rounded-sm bg-primary-foreground" />
            </div>
          )}
          <span className="text-sm font-semibold tracking-tight">{company}</span>
        </div>

        <div className="w-full max-w-md">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {title}
            </h2>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>

          <div className="mt-8 space-y-6">{children}</div>

          {footer && (
            <div className="mt-6 text-center text-sm text-muted-foreground">
              {footer}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const FEATURES = [
  {
    icon: Headphones,
    title: 'Real-time dialer orchestration',
    body: 'Live insight into every campaign, every dialer, every CLI.',
  },
  {
    icon: ShieldCheck,
    title: 'Role-based access control',
    body: 'Fine-grained permissions for resellers, admins, and end users.',
  },
  {
    icon: Zap,
    title: 'Built for scale',
    body: 'Millions of calls, IVR flows, and webhook events per day.',
  },
];
